// assets/predictive-search-popup.js
(() => {
  const debounce = (fn, wait = 200) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const escapeHtml = (str = "") =>
    String(str)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

  async function fetchPredictiveHTML({ q, limit, signal }) {
    const url = new URL("/search/suggest", window.location.origin);
    url.searchParams.set("section_id", "predictive-search");
    url.searchParams.set("q", q);

    url.searchParams.set("resources[type]", "product,collection,page,article");
    url.searchParams.set("resources[limit]", String(limit));
    url.searchParams.set("resources[options][unavailable_products]", "hide");

    const res = await fetch(url.toString(), { signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.text();
  }

  function parsePredictive(htmlText) {
    const doc = new DOMParser().parseFromString(htmlText, "text/html");

    const links = doc.querySelector("[data-ps-links]");
    const products = doc.querySelector("[data-ps-products]");
    const seeAll = doc.querySelector("[data-ps-seeall]");

    return {
      linksHTML: links ? links.innerHTML : "",
      productsHTML: products ? products.innerHTML : "",
      seeAllHTML: seeAll ? seeAll.innerHTML : "",
    };
  }

  class PredictiveSearchPopup extends HTMLElement {
    connectedCallback() {
      if (this.mounted) return;
      this.mounted = true;

      // Read limit from HTML: data-suggest-limit="2"
      this.limit = Number(this.dataset.suggestLimit || 2);

      // Nodes (your popup markup)
      this.input = this.querySelector("[data-search-input]");
      this.linksDefault = this.querySelector("[data-links-default]");
      this.linksPredictive = this.querySelector("[data-links-predictive]");
      this.productsDefault = this.querySelector("[data-products-default]");
      this.productsPredictive = this.querySelector("[data-products-predictive]");
      this.seeAll = this.querySelector("[data-see-all]");
      this.status = this.querySelector("[data-status]");
      this.panel = this.querySelector(".popup-content");
      
      this.btnClear = this.querySelector("[data-search-clear]");
      this.btnClear?.addEventListener("click", (e) => {
        e.preventDefault();
        this.clearInput();
      });

      this.textLoading = this.dataset.textLoading || "Loading…";
      this.textSeeAll = this.dataset.textSeeAll || "See all results for";
      this.textError = this.dataset.textError || "Something went wrong";

      this.btnClose = this.querySelector("[data-search-close]");
      this.abort = null;

      if (!this.input) return;

      this.onInput = debounce(this.onInput.bind(this), 220);
      this.onDocClick = this.onDocClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      this.input.addEventListener("input", this.onInput);
      this.input.addEventListener("keydown", this.onKeyDown);
      document.addEventListener("click", this.onDocClick, true);

      this.btnClose?.addEventListener("click", (e) => {
        e.preventDefault();
        this.close();
      });

      this.input.setAttribute("autocomplete", "off");
      this.input.setAttribute("aria-expanded", "false");
    }

    open() {
      this.classList.add("is-open");
      this.resetToDefault();
      this.updateClearButton();
      requestAnimationFrame(() => this.input?.focus());
    }

    close() {
      this.classList.remove("is-open");
      this.abort?.abort();
      if (this.input) this.input.value = "";
      this.resetToDefault();
      this.input?.setAttribute("aria-expanded", "false");
      this.updateClearButton();
    }

    resetToDefault() {
      this.linksDefault?.classList.remove("hidden");
      this.productsDefault?.classList.remove("hidden");

      if (this.linksPredictive) this.linksPredictive.innerHTML = "";
      if (this.productsPredictive) this.productsPredictive.innerHTML = "";
      if (this.seeAll) this.seeAll.innerHTML = "";

      if (this.status) this.status.hidden = true;
    }

    onDocClick(e) {
      if (!this.classList.contains("is-open")) return;
      if (!this.contains(e.target)) return;
      if (this.panel && !this.panel.contains(e.target)) this.close();
    }

    onKeyDown(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        this.close();
      }
    }

    updateClearButton() {
      if (!this.btnClear) return;
      this.btnClear.hidden = !(this.input && this.input.value.trim().length);
    }

    clearInput() {
      if (!this.input) return;

      this.input.value = "";
      this.abort?.abort();
      this.resetToDefault();
      this.input.setAttribute("aria-expanded", "false");
      this.updateClearButton();
      this.input.focus();
    }

    async onInput() {
      
      const q = (this.input.value || "").trim();
      this.updateClearButton();

      if (!q) {
        this.abort?.abort();
        this.resetToDefault();
        this.input.setAttribute("aria-expanded", "false");
        return;
      }

      this.input.setAttribute("aria-expanded", "true");
      this.linksDefault?.classList.add("hidden");
      this.productsDefault?.classList.add("hidden");

     if (this.status) {
        this.status.textContent = this.textLoading;
        this.status.hidden = false;
      }

      this.abort?.abort();
      this.abort = new AbortController();

      try {
        const htmlText = await fetchPredictiveHTML({
          q,
          limit: this.limit,
          signal: this.abort.signal,
        });

        const { linksHTML, productsHTML, seeAllHTML } = parsePredictive(htmlText);

        if (this.linksPredictive) this.linksPredictive.innerHTML = linksHTML;
        if (this.productsPredictive) this.productsPredictive.innerHTML = productsHTML;

       if (this.seeAll) {
          this.seeAll.innerHTML =
            seeAllHTML ||
            `<a class="ps-seeall" href="/search?q=${encodeURIComponent(q)}">${this.textSeeAll} “${escapeHtml(q)}”</a>`;
        }

        if (this.status) this.status.hidden = true;
      } catch (err) {
        if (err?.name === "AbortError") return;
        console.error(err);
        if (this.status) {
          this.status.textContent = this.textError;
          this.status.hidden = false;
        }
      }
    }
  }

  if (!customElements.get("predictive-search-popup")) {
    customElements.define("predictive-search-popup", PredictiveSearchPopup);
  }

  // Open handler
  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-open-search]");
    if (!btn) return;
    e.preventDefault();
    document.querySelector("predictive-search-popup")?.open();
  });
})();
